/** @jsx jsx */
import { jsx } from '@emotion/core'
import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { Button, ConfirmationOverlay, Icon, Tooltip } from 'components/patterns'
import { getLocalTime } from 'utils/utils'
import { DATE_FORMAT, DATE_TIME_FORMAT } from 'utils/constants'
import {
  MAX_SNAPSHOTS_PER_VEHICLE,
  MAX_SNAPSHOTS_PER_PROFILE,
} from 'selectors/vehicleSnapshots'
import {
  Card,
  ContentWrapper,
  SerialHeader,
  SerialHeaderRow,
  ModelDescription,
  LeadBadge,
  SnapshotList,
  SnapshotRow,
  SnapshotLabelGroup,
  SnapshotLabel,
  RowActions,
  SnapshotSlotLabel,
  SnapshotSlotAvailable,
  actionBtnCss,
  iconBtnCss,
  DeletingOverlay,
  DeletingSpinner,
  CardErrorBanner,
  SnapshotRowSkeleton,
} from './VehicleSnapshotCard.style'
import strings from 'utils/strings'

const MAX_SNAPSHOTS = MAX_SNAPSHOTS_PER_VEHICLE

const VehicleSnapshotCard = ({
  serialNumber,
  snapshots,
  modelDescription,
  isLead,
  isCurrentVehicle,
  saving,
  atProfileLimit,
  deletingIds,
  error,
  onCapture,
  onDelete,
  onView,
  asBuiltData,
  onViewAsBuilt,
  // eslint-disable-next-line no-unused-vars
  showRestoreButton,
}) => {
  const [confirmingId, setConfirmingId] = useState(null)
  const cardRef = useRef(null)

  useEffect(() => {
    if (confirmingId === null) return undefined
    const handleMouseDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setConfirmingId(null)
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [confirmingId])

  const sortedSnapshots = [...snapshots].sort((a, b) => {
    if (!a.createdAt) return 1
    if (!b.createdAt) return -1
    return new Date(a.createdAt) - new Date(b.createdAt)
  })

  const atVehicleLimit = snapshots.length >= MAX_SNAPSHOTS
  const availableSlots = isCurrentVehicle && !atVehicleLimit ? 1 : 0

  const captureDisabled = saving || atProfileLimit || atVehicleLimit

  const isAnyDeleting = deletingIds.length > 0

  const handleRemoveClick = (snapshotId) => {
    setConfirmingId(snapshotId)
  }

  const handleCancelRemove = () => {
    setConfirmingId(null)
  }

  const handleConfirmRemove = () => {
    onDelete(confirmingId)
    setConfirmingId(null)
  }

  return (
    <Card ref={cardRef}>
      {/* Confirm remove overlay */}
      {confirmingId !== null && (
        <ConfirmationOverlay
          message={strings.snapshot_removal_confirmation}
          cancelLabel={strings.no_keep_it}
          confirmLabel={strings.yes_remove_it}
          onCancel={handleCancelRemove}
          onConfirm={handleConfirmRemove}
          dataTestId="confirm-overlay"
          cancelDataTestId="cancel-remove-btn"
          confirmDataTestId="confirm-remove-btn"
        />
      )}

      {/* Deleting overlay */}
      {isAnyDeleting && (
        <DeletingOverlay data-testid="deleting-overlay">
          <DeletingSpinner aria-label="Deleting snapshot…" />
        </DeletingOverlay>
      )}

      <ContentWrapper>
        {/* Header: serial number + LEAD badge (left), model description (right) */}
        <SerialHeaderRow>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              minWidth: 0,
            }}
          >
            <SerialHeader>
              <Link
                to={`/vehicles/${serialNumber}`}
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {serialNumber}
              </Link>
            </SerialHeader>
            {isLead && <LeadBadge>Lead</LeadBadge>}
          </div>
          {modelDescription && (
            <ModelDescription title={modelDescription}>
              {modelDescription}
            </ModelDescription>
          )}
        </SerialHeaderRow>

        {/* Error banner (delete failure for this vehicle) */}
        {error && <CardErrorBanner>{error}</CardErrorBanner>}

        {/* Snapshot rows */}
        <SnapshotList>
          {sortedSnapshots.map((snapshot, index) => {
            const label = `Snapshot ${index + 1}: ${getLocalTime(
              snapshot.createdAt,
              DATE_TIME_FORMAT
            )}`
            const isThisDeleting = deletingIds.includes(snapshot.id)

            return (
              <SnapshotRow key={snapshot.id}>
                <SnapshotLabelGroup>
                  <SnapshotLabel>{label}</SnapshotLabel>
                </SnapshotLabelGroup>
                <RowActions>
                  <button
                    type="button"
                    data-testid={`view-btn-${snapshot.id}`}
                    css={actionBtnCss}
                    onClick={() => onView && onView(snapshot)}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    data-testid={`remove-btn-${snapshot.id}`}
                    css={iconBtnCss}
                    disabled={isThisDeleting}
                    onClick={() => handleRemoveClick(snapshot.id)}
                  >
                    <Icon
                      icon="plus-circle"
                      style={{
                        fontSize: '20px',
                        transform: 'rotate(45deg)',
                      }}
                      fill="outlined"
                      hoverFill="filled"
                    />
                  </button>
                </RowActions>
              </SnapshotRow>
            )
          })}

          {/* Available slot rows — shown when 0–2 snapshots exist */}
          {isCurrentVehicle &&
            Array.from({ length: availableSlots }).map((_, i) => {
              const slotNumber = sortedSnapshots.length + i + 1
              return (
                <SnapshotRow key={`available-${slotNumber}`}>
                  <SnapshotLabelGroup>
                    <SnapshotLabel>
                      <SnapshotSlotLabel>
                        {`Snapshot ${slotNumber}:`}{' '}
                      </SnapshotSlotLabel>
                      <SnapshotSlotAvailable>
                        {atProfileLimit ? 'Not Available' : 'Available'}
                      </SnapshotSlotAvailable>
                    </SnapshotLabel>
                  </SnapshotLabelGroup>
                  <RowActions>
                    <Tooltip
                      title={
                        atProfileLimit
                          ? `Profile snapshot limit reached (${MAX_SNAPSHOTS_PER_PROFILE}/${MAX_SNAPSHOTS_PER_PROFILE}). Delete a snapshot from another vehicle to capture a new one.`
                          : null
                      }
                    >
                      <Button
                        type="primary"
                        size="small"
                        dataTestId={`capture-btn-${slotNumber}`}
                        disabled={captureDisabled}
                        onClick={onCapture}
                      >
                        Capture
                      </Button>
                    </Tooltip>
                  </RowActions>
                </SnapshotRow>
              )
            })}
          {isCurrentVehicle && asBuiltData && asBuiltData.loading && (
            <SnapshotRowSkeleton />
          )}
          {isCurrentVehicle &&
            asBuiltData &&
            !asBuiltData.loading &&
            asBuiltData.error && (
              <SnapshotRow>
                <SnapshotLabelGroup>
                  <SnapshotLabel>
                    <SnapshotSlotLabel>As-Built Record: </SnapshotSlotLabel>
                    <SnapshotSlotAvailable>Not Available</SnapshotSlotAvailable>
                  </SnapshotLabel>
                </SnapshotLabelGroup>
              </SnapshotRow>
            )}
          {isCurrentVehicle &&
            asBuiltData &&
            !asBuiltData.loading &&
            !asBuiltData.error &&
            asBuiltData.buildDate && (
              <SnapshotRow>
                <SnapshotLabelGroup>
                  <SnapshotLabel>
                    {`As-Built Record: ${getLocalTime(
                      asBuiltData.buildDate,
                      DATE_FORMAT
                    )}`}
                  </SnapshotLabel>
                </SnapshotLabelGroup>
                <RowActions>
                  <button
                    type="button"
                    data-testid="view-as-built-btn"
                    css={actionBtnCss}
                    onClick={onViewAsBuilt}
                  >
                    View
                  </button>
                </RowActions>
              </SnapshotRow>
            )}
        </SnapshotList>
      </ContentWrapper>
    </Card>
  )
}

VehicleSnapshotCard.defaultProps = {
  modelDescription: null,
  isLead: false,
  isCurrentVehicle: false,
  saving: false,
  atProfileLimit: false,
  deletingIds: [],
  error: null,
  onCapture: undefined,
  onView: undefined,
  asBuiltData: null,
  onViewAsBuilt: undefined,
  showRestoreButton: false,
}

VehicleSnapshotCard.propTypes = {
  serialNumber: PropTypes.string.isRequired,
  modelDescription: PropTypes.string,
  isLead: PropTypes.bool,
  snapshots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      createdAt: PropTypes.string,
    })
  ).isRequired,
  isCurrentVehicle: PropTypes.bool,
  saving: PropTypes.bool,
  atProfileLimit: PropTypes.bool,
  deletingIds: PropTypes.arrayOf(PropTypes.string),
  error: PropTypes.string,
  onCapture: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  onView: PropTypes.func,
  asBuiltData: PropTypes.shape({
    features: PropTypes.array,
    buildDate: PropTypes.string,
    loading: PropTypes.bool,
    error: PropTypes.string,
  }),
  onViewAsBuilt: PropTypes.func,
  showRestoreButton: PropTypes.bool,
}

export default VehicleSnapshotCard
